import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Building2, Briefcase, Plus, Trash2, Loader2, Calendar, Clock, DollarSign, FileText, Palmtree, Baby, Thermometer, Mail } from 'lucide-react';
import EmailConfiguration from './EmailConfiguration';

const OfficeConfiguration = () => {
  const { getAuthHeaders, getOfficeId, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const officeId = getOfficeId();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [office, setOffice] = useState(null);
  const [configTab, setConfigTab] = useState('general');
  
  // Departments and Job Titles
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [newDepartment, setNewDepartment] = useState({ name: '', description: '' });
  const [newJobTitle, setNewJobTitle] = useState({ title: '', department_id: '' });

  // Config data
  const [configData, setConfigData] = useState({
    employee_id_prefix: '',
    annual_leave_days: 21,
    sick_leave_days: 10,
    maternity_leave_days: 90,
    paternity_leave_days: 14,
    work_start_hour: '08:00',
    work_end_hour: '17:00',
    work_days_per_week: 5,
    overtime_rate: 1.5,
    weekend_rate: 2.0,
    holiday_rate: 2.0,
    public_holidays: [],
    allow_employee_payslip_printing: true,
    letterhead_section1: '',
    letterhead_section2: '',
    letterhead_section3: '',
  });

  const [newHoliday, setNewHoliday] = useState({ name: '', date: '', is_recurring: true });

  useEffect(() => {
    if (officeId) {
      loadOfficeData();
    }
  }, [officeId]);

  const loadOfficeData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      headers['X-Office-ID'] = officeId;

      // Fetch office details
      const officeRes = await fetch(`${API_URL}/api/offices/${officeId}`, { headers });
      if (officeRes.ok) {
        const officeData = await officeRes.json();
        setOffice(officeData);
        
        const settings = officeData.settings || {};
        const leavePolicy = settings.leave_policy || {};
        const workingHours = settings.working_hours || {};
        const letterheadFooter = settings.letterhead_footer || {};

        setConfigData({
          employee_id_prefix: officeData.employee_id_prefix || '',
          annual_leave_days: leavePolicy.annual_leave_days || 21,
          sick_leave_days: leavePolicy.sick_leave_days || 10,
          maternity_leave_days: leavePolicy.maternity_leave_days || 90,
          paternity_leave_days: leavePolicy.paternity_leave_days || 14,
          work_start_hour: workingHours.start || '08:00',
          work_end_hour: workingHours.end || '17:00',
          work_days_per_week: settings.work_days_per_week || 5,
          overtime_rate: settings.overtime_rate || 1.5,
          weekend_rate: settings.weekend_rate || 2.0,
          holiday_rate: settings.holiday_rate || 2.0,
          public_holidays: settings.public_holidays || [],
          allow_employee_payslip_printing: settings.allow_employee_payslip_printing ?? true,
          letterhead_section1: letterheadFooter.section1 || '',
          letterhead_section2: letterheadFooter.section2 || '',
          letterhead_section3: letterheadFooter.section3 || '',
        });
      }

      // Fetch departments
      const deptRes = await fetch(`${API_URL}/api/departments/?office_id=${officeId}`, { headers });
      if (deptRes.ok) {
        setDepartments(await deptRes.json());
      }

      // Fetch job titles
      const jtRes = await fetch(`${API_URL}/api/job-titles/?office_id=${officeId}`, { headers });
      if (jtRes.ok) {
        setJobTitles(await jtRes.json());
      }
    } catch (error) {
      console.error('Error loading office data:', error);
      toast.error('Failed to load office configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      headers['X-Office-ID'] = officeId;

      const response = await fetch(`${API_URL}/api/offices/${officeId}/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          employee_id_prefix: configData.employee_id_prefix,
          settings: {
            leave_policy: {
              annual_leave_days: configData.annual_leave_days,
              sick_leave_days: configData.sick_leave_days,
              maternity_leave_days: configData.maternity_leave_days,
              paternity_leave_days: configData.paternity_leave_days,
            },
            working_hours: {
              start: configData.work_start_hour,
              end: configData.work_end_hour,
            },
            work_days_per_week: configData.work_days_per_week,
            overtime_rate: configData.overtime_rate,
            weekend_rate: configData.weekend_rate,
            holiday_rate: configData.holiday_rate,
            public_holidays: configData.public_holidays,
            allow_employee_payslip_printing: configData.allow_employee_payslip_printing,
            letterhead_footer: {
              section1: configData.letterhead_section1,
              section2: configData.letterhead_section2,
              section3: configData.letterhead_section3,
            },
          },
        }),
      });

      if (response.ok) {
        toast.success('Configuration saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartment.name.trim()) return;
    try {
      const headers = getAuthHeaders();
      headers['X-Office-ID'] = officeId;
      const response = await fetch(`${API_URL}/api/departments/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...newDepartment, office_id: officeId })
      });
      if (response.ok) {
        toast.success('Department added');
        setNewDepartment({ name: '', description: '' });
        loadOfficeData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add department');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    try {
      const headers = getAuthHeaders();
      headers['X-Office-ID'] = officeId;
      const response = await fetch(`${API_URL}/api/departments/${deptId}`, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        toast.success('Department deleted');
        loadOfficeData();
      }
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  const handleAddJobTitle = async () => {
    if (!newJobTitle.title.trim()) return;
    try {
      const headers = getAuthHeaders();
      headers['X-Office-ID'] = officeId;
      const response = await fetch(`${API_URL}/api/job-titles/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...newJobTitle, office_id: officeId })
      });
      if (response.ok) {
        toast.success('Job title added');
        setNewJobTitle({ title: '', department_id: '' });
        loadOfficeData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add job title');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleDeleteJobTitle = async (titleId) => {
    try {
      const headers = getAuthHeaders();
      headers['X-Office-ID'] = officeId;
      const response = await fetch(`${API_URL}/api/job-titles/${titleId}`, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        toast.success('Job title deleted');
        loadOfficeData();
      }
    } catch (error) {
      toast.error('Failed to delete job title');
    }
  };

  const handleAddHoliday = () => {
    if (!newHoliday.name.trim() || !newHoliday.date) return;
    setConfigData(prev => ({
      ...prev,
      public_holidays: [...prev.public_holidays, { ...newHoliday, id: Date.now().toString() }]
    }));
    setNewHoliday({ name: '', date: '', is_recurring: true });
  };

  const handleRemoveHoliday = (index) => {
    setConfigData(prev => ({
      ...prev,
      public_holidays: prev.public_holidays.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="office-configuration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Office Configuration</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {office?.name} - Configure departments, job titles, leave policy, and more
          </p>
        </div>
        <Button onClick={handleSaveConfig} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Changes
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Tabs value={configTab} onValueChange={setConfigTab} className="w-full">
            <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-muted rounded-lg">
              <TabsTrigger value="general" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-general">General</TabsTrigger>
              <TabsTrigger value="departments" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-departments">Departments</TabsTrigger>
              <TabsTrigger value="jobtitles" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-jobtitles">Job Titles</TabsTrigger>
              <TabsTrigger value="leave" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-leave">Leave</TabsTrigger>
              <TabsTrigger value="hours" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-hours">Hours</TabsTrigger>
              <TabsTrigger value="holidays" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-holidays">Holidays</TabsTrigger>
              <TabsTrigger value="payroll" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-payroll">Payroll</TabsTrigger>
              <TabsTrigger value="letterhead" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-letterhead">Letter</TabsTrigger>
              <TabsTrigger value="email" className="text-xs px-3 py-1.5 data-[state=active]:bg-background" data-testid="tab-email">Email</TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Employee ID Prefix</Label>
                  <Input
                    value={configData.employee_id_prefix}
                    onChange={(e) => setConfigData({ ...configData, employee_id_prefix: e.target.value.toUpperCase() })}
                    placeholder="e.g., ASK"
                    maxLength={5}
                  />
                  <p className="text-xs text-muted-foreground">Prefix for employee IDs (e.g., ASK-001)</p>
                </div>
              </div>
            </TabsContent>

            {/* Departments Tab */}
            <TabsContent value="departments" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Department name"
                  value={newDepartment.name}
                  onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })}
                />
                <Input
                  placeholder="Description (optional)"
                  value={newDepartment.description}
                  onChange={(e) => setNewDepartment({ ...newDepartment, description: e.target.value })}
                  className="flex-1"
                />
                <Button onClick={handleAddDepartment} disabled={!newDepartment.name.trim()}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                {departments.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No departments yet. Add your first department above.</p>
                  </div>
                ) : (
                  departments.map((dept) => (
                    <div key={dept.id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                      <div>
                        <p className="font-medium">{dept.name}</p>
                        {dept.description && <p className="text-sm text-muted-foreground">{dept.description}</p>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDepartment(dept.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Job Titles Tab */}
            <TabsContent value="jobtitles" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Job title"
                  value={newJobTitle.title}
                  onChange={(e) => setNewJobTitle({ ...newJobTitle, title: e.target.value })}
                />
                <Select value={newJobTitle.department_id || "none"} onValueChange={(v) => setNewJobTitle({ ...newJobTitle, department_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleAddJobTitle} disabled={!newJobTitle.title.trim()}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                {jobTitles.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No job titles yet. Add your first job title above.</p>
                  </div>
                ) : (
                  jobTitles.map((jt) => (
                    <div key={jt.id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                      <div>
                        <p className="font-medium">{jt.title}</p>
                        {jt.department_name && <p className="text-sm text-muted-foreground">{jt.department_name}</p>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteJobTitle(jt.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Leave Policy Tab */}
            <TabsContent value="leave" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Palmtree className="h-4 w-4" /> Annual Leave (days)</Label>
                  <Input
                    type="number"
                    value={configData.annual_leave_days}
                    onChange={(e) => setConfigData({ ...configData, annual_leave_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Thermometer className="h-4 w-4" /> Sick Leave (days)</Label>
                  <Input
                    type="number"
                    value={configData.sick_leave_days}
                    onChange={(e) => setConfigData({ ...configData, sick_leave_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Baby className="h-4 w-4" /> Maternity Leave (days)</Label>
                  <Input
                    type="number"
                    value={configData.maternity_leave_days}
                    onChange={(e) => setConfigData({ ...configData, maternity_leave_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Baby className="h-4 w-4" /> Paternity Leave (days)</Label>
                  <Input
                    type="number"
                    value={configData.paternity_leave_days}
                    onChange={(e) => setConfigData({ ...configData, paternity_leave_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Working Hours Tab */}
            <TabsContent value="hours" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Clock className="h-4 w-4" /> Work Start Time</Label>
                  <Input
                    type="time"
                    value={configData.work_start_hour}
                    onChange={(e) => setConfigData({ ...configData, work_start_hour: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Clock className="h-4 w-4" /> Work End Time</Label>
                  <Input
                    type="time"
                    value={configData.work_end_hour}
                    onChange={(e) => setConfigData({ ...configData, work_end_hour: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Work Days Per Week</Label>
                  <Select value={String(configData.work_days_per_week)} onValueChange={(v) => setConfigData({ ...configData, work_days_per_week: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 days (Mon-Fri)</SelectItem>
                      <SelectItem value="6">6 days (Mon-Sat)</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Holidays Tab */}
            <TabsContent value="holidays" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Holiday name"
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                />
                <Input
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                />
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newHoliday.is_recurring}
                    onCheckedChange={(checked) => setNewHoliday({ ...newHoliday, is_recurring: checked })}
                  />
                  <span className="text-sm">Recurring</span>
                </div>
                <Button onClick={handleAddHoliday} disabled={!newHoliday.name.trim() || !newHoliday.date}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="border rounded-lg divide-y max-h-[250px] overflow-y-auto">
                {configData.public_holidays.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No public holidays configured</p>
                  </div>
                ) : (
                  configData.public_holidays.map((holiday, index) => (
                    <div key={holiday.id || index} className="flex items-center justify-between p-3">
                      <div>
                        <p className="font-medium">{holiday.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {holiday.date} {holiday.is_recurring && '(Recurring annually)'}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveHoliday(index)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Payroll Tab */}
            <TabsContent value="payroll" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> Overtime Rate (x)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configData.overtime_rate}
                    onChange={(e) => setConfigData({ ...configData, overtime_rate: parseFloat(e.target.value) || 1.5 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weekend Rate (x)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configData.weekend_rate}
                    onChange={(e) => setConfigData({ ...configData, weekend_rate: parseFloat(e.target.value) || 2.0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Holiday Rate (x)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={configData.holiday_rate}
                    onChange={(e) => setConfigData({ ...configData, holiday_rate: parseFloat(e.target.value) || 2.0 })}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={configData.allow_employee_payslip_printing}
                    onCheckedChange={(checked) => setConfigData({ ...configData, allow_employee_payslip_printing: checked })}
                  />
                  <Label>Allow employees to print payslips</Label>
                </div>
              </div>
            </TabsContent>

            {/* Letterhead Tab */}
            <TabsContent value="letterhead" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">Configure the 3-section footer that appears on official letters</p>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><FileText className="h-4 w-4" /> Footer Section 1</Label>
                  <Textarea
                    value={configData.letterhead_section1}
                    onChange={(e) => setConfigData({ ...configData, letterhead_section1: e.target.value })}
                    placeholder="e.g., Directors: John Doe, Jane Doe"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Footer Section 2</Label>
                  <Textarea
                    value={configData.letterhead_section2}
                    onChange={(e) => setConfigData({ ...configData, letterhead_section2: e.target.value })}
                    placeholder="e.g., Registered Office: 123 Main St"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Footer Section 3</Label>
                  <Textarea
                    value={configData.letterhead_section3}
                    onChange={(e) => setConfigData({ ...configData, letterhead_section3: e.target.value })}
                    placeholder="e.g., TIN: 123456789"
                    rows={2}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Email Settings Tab */}
            <TabsContent value="email" className="mt-4">
              <EmailConfiguration officeLevel={true} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default OfficeConfiguration;
